import { useCallback, useEffect, useState } from "react";

import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import { fetchActiveLiveStream, type PublicLiveStreamItem } from "@/features/public/api";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    dateStyle: "full",
    timeStyle: "short"
  });
}

export default function LivePage() {
  const [stream, setStream] = useState<PublicLiveStreamItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStream = useCallback(async () => {
    setError(null);

    try {
      const activeStream = await fetchActiveLiveStream();
      setStream(activeStream);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de charger le live en cours");
      setStream(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStream();
    const intervalId = window.setInterval(() => {
      void loadStream();
    }, 10000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadStream();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadStream]);

  useRealtimeInvalidation({
    topics: ["lives"],
    onInvalidate: () => {
      void loadStream();
    }
  });

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <Navbar />

      <main className="pt-28 pb-20">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[36px] bg-[linear-gradient(135deg,#111111_0%,#2B2B2B_100%)] text-white border border-white/5 overflow-hidden shadow-[0_24px_80px_rgba(17,24,39,0.16)]">
            <div className="px-6 md:px-10 py-8 md:py-10 border-b border-white/10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/15 border border-red-400/40 text-red-100 text-sm font-semibold uppercase tracking-[0.18em]">
                    <span className="relative flex h-3 w-3">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                    </span>
                    Live en Direct
                  </div>
                  <h1 className="mt-5 text-3xl md:text-5xl font-bold">
                    {stream ? stream.title : "Diffusion en direct ASC Sonatel"}
                  </h1>
                  <p className="mt-3 text-sm md:text-base text-white/70 max-w-3xl">
                    {stream
                      ? "Diffusion intégrée en direct. Vous pouvez suivre le live sans quitter l'application."
                      : "Dès qu'un live est lancé depuis l'administration, il apparaît ici automatiquement pour tous les visiteurs."}
                  </p>
                </div>

                {stream ? (
                  <div className="rounded-2xl bg-white/5 border border-white/10 px-5 py-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/45">Démarré</div>
                    <div className="mt-2 text-sm text-white/85">{formatDateTime(stream.startedAt)}</div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              {loading ? (
                <div className="aspect-video rounded-[28px] bg-white/5 animate-pulse"></div>
              ) : error ? (
                <div className="rounded-3xl border border-red-400/30 bg-red-500/10 px-6 py-10 text-center text-red-100">
                  {error}
                </div>
              ) : stream ? (
                <div className="aspect-video rounded-[28px] overflow-hidden border border-white/10 bg-black">
                  <iframe
                    src={stream.embedUrl}
                    title={stream.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-16 text-center">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 text-white/70 flex items-center justify-center">
                    <i className="ri-live-line text-3xl"></i>
                  </div>
                  <h2 className="mt-5 text-2xl font-bold">Aucun live en cours</h2>
                  <p className="mt-3 text-sm text-white/65 max-w-xl mx-auto">
                    Le prochain direct apparaîtra ici automatiquement. Vous n'avez pas besoin d'être connecté pour en profiter.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
